import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaCreditCard, FaSpinner, FaCheckCircle, FaFileContract, FaCalendarAlt, FaDesktop } from 'react-icons/fa';
import EULA from '../auth/EULA';
import CreditPanel from './CreditPanel';
import useCreditMode from '../../hooks/useCreditMode';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Single checkout client for credit purchases. Credit mode is decided
// server-side, and `packageId` only names which fixed pack to buy -- the credit
// quantity and price are resolved from credit_settings, so nothing this function
// sends can change what is charged.
export const requestCreditCheckout = async (token, packageId) => {
  const response = await fetch(`${API_BASE}/api/payment/create-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(packageId ? { package_id: packageId } : {}),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to start checkout');
  return data;
};

const PaymentPanel = () => {
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [environment, setEnvironment] = useState('development');
  const [showEULA, setShowEULA] = useState(false);
  const [acceptedEULA, setAcceptedEULA] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [evaluationStatus, setEvaluationStatus] = useState(null);
  const [applyingEvaluation, setApplyingEvaluation] = useState(false);
  const [evalRequestSuccess, setEvalRequestSuccess] = useState(false);
  
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const creditMode = useCreditMode();

  const PRICE_PER_MONTH = 2000.00; // $2000 per month

  const fetchEvaluationStatus = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/license/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEvaluationStatus(data.evaluation_status);
      }
    } catch (err) {
      console.error('Error fetching evaluation status:', err);
    }
  }, [token]);

  // Load environment and check payment status on component load
  useEffect(() => {
    const loadEnvironmentAndCheckPayment = async () => {
      try {
        // Fetch environment from products endpoint
        const envResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/products`);
        if (envResponse.ok) {
          const envData = await envResponse.json();
          if (envData.environment) {
            setEnvironment(envData.environment);
          }
        }
      } catch (err) {
        console.error('Error fetching environment:', err);
      }

      // Check if user just returned from Stripe payment
      const urlParams = new URLSearchParams(window.location.search);
      const paymentSuccess = urlParams.get('payment_success');
      const storedUserId = localStorage.getItem('payment_user_id');
      
      if (paymentSuccess === 'true' && user?.id && storedUserId === user.id) {
        try {
          // Check payment status
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/check-status/${user.id}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.has_license) {
              setSuccess(true);
              
              localStorage.removeItem('payment_user_id');
              window.history.replaceState({}, document.title, window.location.pathname);
              
              setTimeout(() => {
                setSuccess(false);
                window.location.reload();
              }, 5000);
            }
          }
        } catch (err) {
          console.error('Error checking payment status:', err);
        }
      }
    };

    loadEnvironmentAndCheckPayment();
    fetchEvaluationStatus();
  }, [token, user, fetchEvaluationStatus]);

  const handleApplyEvaluation = async () => {
    setApplyingEvaluation(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/license/evaluation/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        await response.json();
        setEvalRequestSuccess(true);
        setTimeout(() => {
          setEvalRequestSuccess(false);
          fetchEvaluationStatus();
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to apply for evaluation license');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setApplyingEvaluation(false);
    }
  };

  const totalPrice = selectedMonths * PRICE_PER_MONTH;
  const totalDays = selectedMonths * 30;

  const handleSubmit = async (event) => {
    event.preventDefault();

    setProcessing(true);
    setError('');

    if (!token) {
      setError('You must be logged in to make a payment. Please login first.');
      setProcessing(false);
      return;
    }

    try {
      // Create payment intent or get redirect URL
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          months: selectedMonths
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const responseData = await response.json();

      // Update environment state from backend response
      if (responseData.environment) {
        setEnvironment(responseData.environment);
      }

      // Check if we need to redirect to Stripe (production)
      if (responseData.redirect_to_stripe) {
        // Store user ID for callback handling
        localStorage.setItem('payment_user_id', responseData.user_id);
        
        // Redirect to Stripe payment page
        window.location.href = responseData.stripe_url;
        return;
      }

      // Store payment intent ID for EULA acceptance
      if (responseData.payment_intent_id) {
        setPaymentIntentId(responseData.payment_intent_id);
      }

      // Check if EULA is required
      if (responseData.requires_eula) {
        setShowEULA(true);
        setProcessing(false);
        return;
      }

      setError('Unexpected response from server');
      setProcessing(false);
    } catch (err) {
      setError('Payment failed. Please try again.');
      console.error('Payment error:', err);
      setProcessing(false);
    }
  };

  const handleEULAAccept = async () => {
    if (!acceptedEULA) {
      setError('You must accept the EULA to continue');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Accept EULA
      const eulaResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/accept-eula`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId,
          accepted: true
        })
      });

      if (!eulaResponse.ok) {
        const errorData = await eulaResponse.json();
        throw new Error(errorData.error || 'Failed to accept EULA');
      }

      // Confirm payment after EULA acceptance
      const confirmResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId
        })
      });

      if (confirmResponse.ok) {
        setShowEULA(false);
        setSuccess(true);
        
        setTimeout(() => {
          setSuccess(false);
          window.location.reload();
        }, 5000);
      } else {
        const errorData = await confirmResponse.json();
        setError(errorData.error || 'Payment confirmation failed');
      }
    } catch (err) {
      setError('Failed to complete payment. Please try again.');
      console.error('EULA/Payment error:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Credit mode: sell credits, not license tiers/durations.
  if (creditMode) {
    return <CreditPanel />;
  }

  if (evalRequestSuccess) {
    return (
      <div className="text-center py-8">
        <FaCheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h3 className={`text-lg font-semibold mb-2 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Request Submitted!
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {evaluationStatus?.has_applied 
            ? 'Your extension request has been sent to the admin for review.'
            : 'Your evaluation request has been sent to the admin for review.'}
        </p>
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          You will be notified once it's approved.
        </p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <FaCheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h3 className={`text-lg font-semibold mb-2 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Payment Successful!
        </h3>
        
        <p className={`text-sm mb-4 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Your license has been purchased. Download ZapRF and sign in to activate it automatically.
        </p>
        
        <div className={`max-w-sm mx-auto p-4 rounded-lg border mb-4 ${
          isDarkMode 
            ? 'bg-blue-900/20 border-blue-500/30' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-center mb-2">
            <FaDesktop className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              How to Activate
            </span>
          </div>
          <ol className={`text-xs text-left space-y-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <li>1. Download and install ZapRF</li>
            <li>2. Launch the app and sign in</li>
            <li>3. Your license activates automatically</li>
          </ol>
        </div>
        
        <p className={`text-xs ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          The page will refresh shortly to update your license status.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Evaluation License Section */}
      <div className={`p-6 rounded-xl border transition-all duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-blue-900/20 to-brand-purple/10 border-blue-700/30' 
          : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200/50'
      }`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1">
            <h3 className={`text-lg font-bold mb-2 flex items-center ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <FaFileContract className="mr-2 text-blue-500" />
              Evaluation License
            </h3>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {evaluationStatus?.has_applied 
                ? (evaluationStatus.status === 'approved' 
                    ? "You already have an evaluation license. Need more time? You can request an extension."
                    : evaluationStatus.status === 'pending'
                    ? "Your evaluation request is currently being reviewed by our administrator."
                    : "Your previous evaluation request was processed. You can request an extension if needed.")
                : "Interested in trying ZapRF before purchasing? Apply for a free 30-day evaluation license. Once approved, simply download the app and sign in to activate."}
            </p>
          </div>
          
          <div className="flex-shrink-0">
            {evaluationStatus?.status === 'pending' ? (
              <div className="px-6 py-3 rounded-lg bg-yellow-500/20 border border-yellow-500/30 text-yellow-600 dark:text-yellow-400 text-sm font-semibold flex items-center">
                <FaSpinner className="animate-spin mr-2" />
                Review Pending
              </div>
            ) : (
              <button
                onClick={handleApplyEvaluation}
                disabled={applyingEvaluation}
                className={`w-full md:w-auto px-8 py-3 rounded-lg font-bold text-sm transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center ${
                  evaluationStatus?.has_applied
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-brand-purple hover:bg-brand-purple-dark text-white'
                } disabled:opacity-50`}
              >
                {applyingEvaluation ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    {evaluationStatus?.has_applied ? 'Request Extension' : 'Apply for Evaluation'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {evaluationStatus?.has_applied && evaluationStatus.status === 'approved' && (
          <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'}`}>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Note: Evaluation licenses are valid for 30 days. Extensions are subject to administrative approval.
            </p>
          </div>
        )}
      </div>

      {/* License Period Selection */}
      <div className={`p-4 rounded-xl border transition-all duration-300 ${
        isDarkMode 
          ? 'bg-gray-800/30 border-gray-700/30' 
          : 'bg-white/50 border-gray-200/50'
      }`}>
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-gradient-to-r from-brand-purple to-brand-red rounded-lg">
            <FaCalendarAlt className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold mb-1 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              ZapRF License
            </h3>
            <p className={`text-sm mb-3 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              $2000.00 per month - Select your license validity period
            </p>
            
            {/* Month Selector */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                License Validity (Months)
              </label>
              <select
                value={selectedMonths}
                onChange={(e) => setSelectedMonths(parseInt(e.target.value))}
                className={`w-full px-4 py-2 rounded-lg border text-sm transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gray-800/50 border-gray-600 text-white focus:border-brand-red focus:ring-2 focus:ring-brand-red/20'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20'
                } focus:outline-none`}
              >
                {[...Array(12)].map((_, i) => {
                  const months = i + 1;
                  return (
                    <option key={months} value={months}>
                      {months} {months === 1 ? 'Month' : 'Months'} ({months * 30} days)
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div className="text-2xl font-bold text-brand-red mb-3">
              ${totalPrice.toFixed(2)}
            </div>
            
            <ul className="space-y-1">
              <li className={`text-sm flex items-center ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <FaCheckCircle className="w-3 h-3 text-green-400 mr-2" />
                {selectedMonths} {selectedMonths === 1 ? 'month' : 'months'} validity ({totalDays} days)
              </li>
              <li className={`text-sm flex items-center ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <FaCheckCircle className="w-3 h-3 text-green-400 mr-2" />
                Full feature access
              </li>
              <li className={`text-sm flex items-center ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <FaCheckCircle className="w-3 h-3 text-green-400 mr-2" />
                Email support
              </li>
              <li className={`text-sm flex items-center ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <FaCheckCircle className="w-3 h-3 text-green-400 mr-2" />
                License management tools
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Payment Info */}
        <div className={`p-4 rounded-lg border transition-all duration-300 ${
          isDarkMode 
            ? 'bg-gray-800/30 border-gray-700/30' 
            : 'bg-white/50 border-gray-200/50'
        }`}>
          <label className={`block text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {environment === 'production' ? 'Secure Payment' : 'Demo Payment (No real card needed)'}
          </label>
          <div className={`p-3 rounded-lg border ${
            isDarkMode 
              ? 'bg-gray-900/50 border-gray-600' 
              : 'bg-white border-gray-300'
          }`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {environment === 'production' 
                ? 'You will be redirected to Stripe for secure payment processing. After payment, you will need to accept the EULA before license generation.'
                : 'This is a demo payment system. Click "Purchase License" to simulate payment. You will then need to accept the EULA.'
              }
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={processing}
          className="w-full flex items-center justify-center py-3 px-4 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <>
              <FaSpinner className="animate-spin w-4 h-4 mr-2" />
              Processing...
            </>
          ) : (
            <>
              <FaCreditCard className="w-4 h-4 mr-2" />
              Purchase License - ${totalPrice.toFixed(2)}
            </>
          )}
        </button>
      </form>

      {/* Security Notice */}
      <div className={`p-3 rounded-lg text-xs ${
        isDarkMode 
          ? 'bg-gray-800/30 text-gray-400' 
          : 'bg-gray-50 text-gray-500'
      }`}>
        🔒 {environment === 'production' 
          ? 'All payments are processed securely through Stripe. Your payment information is encrypted and protected.'
          : 'This is a demo application. No real payment processing is performed.'
        }
      </div>

      {/* EULA Modal */}
      {showEULA && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-gradient-to-br from-brand-purple to-brand-red-dark rounded-2xl p-1 max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className={`rounded-2xl flex flex-col h-full ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
              {/* Modal Header - Fixed */}
              <div className="flex-shrink-0 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className={`text-lg sm:text-2xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <FaFileContract className="mr-2 sm:mr-3 text-base sm:text-xl" />
                  End User License Agreement
                </h2>
              </div>

              {/* Modal Content - Scrollable with Fixed Height */}
              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 min-h-0 custom-scrollbar" style={{maxHeight: 'calc(90vh - 280px)'}}>
                <div className="pr-2">
                  <EULA />
                </div>
              </div>

              {/* Modal Footer - Fixed */}
              <div className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {error && (
                  <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg text-sm mb-4">
                    {error}
                  </div>
                )}
                
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="acceptEULA"
                    checked={acceptedEULA}
                    onChange={(e) => setAcceptedEULA(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-brand-red focus:ring-2 focus:ring-brand-red cursor-pointer"
                  />
                  <label htmlFor="acceptEULA" className={`ml-3 text-sm cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    I have read and agree to the <span className="font-semibold">End User License Agreement (EULA)</span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowEULA(false);
                      setAcceptedEULA(false);
                    }}
                    disabled={processing}
                    className={`px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isDarkMode 
                        ? 'text-white/80 hover:text-white border border-white/30 hover:bg-white/10' 
                        : 'text-gray-700 border border-gray-300 hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEULAAccept}
                    disabled={!acceptedEULA || processing}
                    className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {processing ? (
                      <>
                        <FaSpinner className="animate-spin w-4 h-4 mr-2" />
                        Processing...
                      </>
                    ) : (
                      'Accept & Confirm Payment'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPanel; 